import i18n from 'config/i18n';
import { areIntervalsOverlapping } from 'date-fns';
import { isNil } from 'ramda';
import * as yup from 'yup';

import { CostCenterDetailFlag } from '../../types';

import { getLocale } from 'utils/date';

const t = i18n.getFixedT(getLocale());

export const planUnitSchema = yup.object({
  type: yup.string().required(t('validation.valueIsRequired')).default(''),
  category: yup.string().nullable().default(''),
  sortOrderAgency: yup.string(),
  colorCode: yup.string(),

  REQUEST_EXCLUDE: yup.boolean(),
  privilegedEmailAddress: yup
    .string()
    .test('is-valid', 'Invalid email addresses', (value) => {
      if (!value) {
        return true;
      }
      const emailSchema = yup.string().email();
      for (const email of value.split(';')) {
        if (!emailSchema.isValidSync(email.trim())) {
          return false;
        }
      }
      return true;
    })
});

const checkBoxSchema = {};

for (const key of Object.keys(CostCenterDetailFlag)) {
  checkBoxSchema[key] = yup.boolean().default(false);
}

const validateOverlap = (currentRange, rangeArray) => {
  let isValid = true;

  rangeArray.forEach((fieldSet) => {
    if (
      fieldSet.brDynId === currentRange.brDynId ||
      !currentRange.valid.from ||
      !currentRange.valid.to ||
      !fieldSet.valid.from ||
      !fieldSet.valid.to
    ) {
      return;
    }

    if (
      areIntervalsOverlapping(
        {
          start: new Date(currentRange.valid.from),
          end: new Date(currentRange.valid.to)
        },
        {
          start: new Date(fieldSet.valid.from),
          end: new Date(fieldSet.valid.to)
        }
      )
    ) {
      isValid = false;
    }
  });

  return isValid;
};

const validSchema = yup.object({
  valid: yup.object({
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null)
      .min(yup.ref<Date>('from'), t('premiumPays.form.errors.invalidInterval'))
  })
});

const overlapValidator: yup.TestFunction<Date | null, any> = (
  val: Date | null,
  context: yup.TestContext<any>
) => {
  // we have 3 level deep object schema we get the second from the context array
  // eslint-disable-next-line
  // @ts-ignore
  const current = context.from[1].value;

  const arrayToCompare =
    // eslint-disable-next-line
    // @ts-ignore
    context.from.slice(-1)[0].value.costCenterDetails;

  return validateOverlap(current, arrayToCompare);
};

const validSchemaWithOverlapValidation = yup.object({
  valid: yup.object({
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null)
      .test(
        'validateOverlap',
        t('db.error.exclusion_violation'),
        overlapValidator
      ),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null)
      .min(yup.ref<Date>('from'), t('premiumPays.form.errors.invalidInterval'))
      .test(
        'validateOverlap',
        t('db.error.exclusion_violation'),
        overlapValidator
      )
  })
});

export const createCostCenterDetailSchema = yup.object({
  code: yup.string().required(t('validation.valueIsRequired')),
  name: yup.string().required(t('validation.valueIsRequired')),
  nameShort: yup.string().required(t('validation.valueIsRequired')),
  codeExt: yup.string(),
  codeExtSecondary: yup.string(),
  codeImp: yup.string(),
  codeImpSecondary: yup.string(),
  finBrDynId: yup
    .object()
    .shape({
      value: yup.number().nullable().default(null)
    })
    .nullable(),
  parentBrDynId: yup
    .object()
    .shape({
      value: yup.number().nullable().default(null)
    })
    .nullable(),
  benefitStartDate: yup
    .date()
    .nullable()
    .default(null)
    .typeError(
      t('validationDateInvalid', {
        name: t('costCenterDetail.importStartDate')
      })
    ),
  flags: yup.object({ ...checkBoxSchema }),
  employerId: yup.number().nullable().default(null)
});

export const createPlanUnitSchema = planUnitSchema.concat(
  createCostCenterDetailSchema.concat(validSchema)
);

export const editPlanUnitSchema = planUnitSchema.concat(
  yup.object().shape({
    costCenterDetails: yup.lazy((arr) => {
      if (isNil(arr)) {
        return yup.array();
      }

      return yup
        .array()
        .of(
          createCostCenterDetailSchema.concat(validSchemaWithOverlapValidation)
        );
    })
  })
);
